import { ChatContainer, MessageList } from '@chatscope/chat-ui-kit-react';
import { ChatComposer } from '@features/chat/chat-container';
import Box from '@mui/material/Box';
import Portal from '@mui/material/Portal';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Buffer } from 'buffer';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Socket } from 'socket.io-client';

import ChatDialogHeader from './chat-dialog-header';
import ChatMessageList from './chat-message-list';
import type { ChatUser } from './chatDialogsTypes';
import { useChatSession } from './useChatSession';
import { useDraggablePanel } from './useDraggablePanel';

const browserWindow = window as Window & { Buffer?: typeof Buffer };
browserWindow.Buffer = browserWindow.Buffer || Buffer;

interface ChatDialogsProps {
  handleChatDialogClose: () => void;
  userData?: ChatUser;
  socket?: Socket;
  showChatBox: boolean;
}

const ChatDialogs = ({
  handleChatDialogClose,
  userData,
  socket,
  showChatBox,
}: ChatDialogsProps): JSX.Element => {
  const chatUser = userData as ChatUser;
  const chatSocket = socket as Socket;
  const session = useChatSession(chatUser, chatSocket);

  const { t } = useTranslation();
  const theme = useTheme();
  const canDrag = useMediaQuery(theme.breakpoints.up('sm'));
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useDraggablePanel(panelRef, '.cs-conversation-header', canDrag);

  if (!showChatBox) {
    return <></>;
  }

  // A floating, non-modal panel: the rest of the page stays clickable
  return (
    <Portal>
      <Box
        ref={panelRef}
        role="dialog"
        aria-label={t('chat.brand')}
        onPointerDown={drag.onPointerDown}
        onKeyDown={(event) => {
          if (event.key === 'Escape') handleChatDialogClose();
        }}
        sx={{
          position: 'fixed',
          zIndex: 'modal',
          bottom: { xs: 8, sm: 20 },
          right: { xs: 8, sm: 20 },
          left: { xs: 8, sm: 'auto' },
          height: { xs: 'min(70vh, 560px)', sm: 560 },
          maxHeight: 'calc(100vh - 88px)',
          width: { xs: 'auto', sm: 380 },
          maxWidth: 'calc(100vw - 24px)',
          overflow: 'hidden',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45)',
          transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)`,
          userSelect: drag.dragging ? 'none' : undefined,
          '& .cs-conversation-header': canDrag
            ? {
                cursor: drag.dragging ? 'grabbing' : 'grab',
                touchAction: 'none',
              }
            : undefined,
        }}
      >
        <Box
          className="r8tr-web-chat"
          sx={{
            display: 'flex',
            height: '100%',
            minHeight: 0,
            width: '100%',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <ChatContainer>
            <ChatDialogHeader
              as="ConversationHeader"
              onClose={handleChatDialogClose}
            />
            <MessageList>
              <ChatMessageList
                messages={session.messageArray}
                userId={chatUser.id}
                onRetry={session.retryMessage}
              />
            </MessageList>
            <ChatComposer
              as="MessageInput"
              voice={session.voice}
              onSendVoice={session.sendVoice}
              fileInputRef={session.fileInputRef}
              handleFileTypeField={session.handleFileTypeField}
              setMgInputValue={session.setMgInputValue}
              handlePaste={session.handlePaste}
              msgInputValue={session.msgInputValue}
              handleSend={session.handleSend}
            />
          </ChatContainer>
        </Box>
      </Box>
    </Portal>
  );
};

export default ChatDialogs;
